import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Check, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

/**
 * Landing page Stripe redirects to after a successful checkout.
 * Polls /api/payments/status/{session_id} until payment_status flips to
 * "paid" (webhook or fallback poll — whichever wins first). Retries with
 * short backoff so the customer never sees a stale "pending" spinner.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('pending');
  const [tx, setTx] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return; }
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTx(data);
        if (data.payment_status === 'paid') { setState('paid'); return; }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') { setState('failed'); return; }
        if (attempts.current >= 15) { setState('timeout'); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (attempts.current >= 5) { setState('error'); return; }
        timer = setTimeout(poll, 3000);
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [sessionId]);

  return (
    <div className="container-x py-16 md:py-24" data-testid="payment-success">
      <div className="max-w-lg mx-auto text-center">
        {state === 'pending' && (
          <>
            <Loader2 size={40} className="mx-auto animate-spin text-neutral-400" />
            <p className="eyebrow mt-6">Confirming payment</p>
            <h1 className="h-display text-3xl md:text-4xl mt-2">One moment…</h1>
            <p className="sans text-sm text-neutral-500 mt-3">
              Stripe is finalising your payment. This normally takes a few seconds.
            </p>
          </>
        )}
        {state === 'paid' && (
          <>
            <div className="mx-auto w-16 h-16 rounded-full bg-[color:var(--ije-pink-tint)] flex items-center justify-center">
              <Check size={28} className="text-black" />
            </div>
            <p className="eyebrow mt-6">Payment received</p>
            <h1 className="h-display text-3xl md:text-4xl mt-2">Thank you.</h1>
            <p className="sans text-sm text-neutral-500 mt-3">
              Your order {tx?.order_number ? <><span className="font-mono text-neutral-900">{tx.order_number}</span> is </> : 'is '}
              marked paid. A receipt was sent to your inbox by Stripe.
            </p>
            {tx?.order_id && (
              <Link to={`/orders/${tx.order_id}`} className="btn-primary inline-flex mt-6" data-testid="payment-success-order-link">
                View order
              </Link>
            )}
          </>
        )}
        {(state === 'failed' || state === 'error') && (
          <>
            <AlertTriangle size={40} className="mx-auto text-neutral-500" />
            <p className="eyebrow mt-6">Something went wrong</p>
            <h1 className="h-display text-3xl md:text-4xl mt-2">Payment not completed</h1>
            <p className="sans text-sm text-neutral-500 mt-3">
              We couldn't confirm your payment. If your card was charged, please contact us at{' '}
              <a href="mailto:priscila@ijehaus.com" className="link-pink">priscila@ijehaus.com</a>.
            </p>
            {tx?.order_id && (
              <Link to={`/orders/${tx.order_id}`} className="btn-outline inline-flex mt-6">Back to order</Link>
            )}
          </>
        )}
        {state === 'timeout' && (
          <>
            <Loader2 size={40} className="mx-auto text-neutral-400" />
            <p className="eyebrow mt-6">Still processing</p>
            <h1 className="h-display text-3xl md:text-4xl mt-2">This is taking longer than usual</h1>
            <p className="sans text-sm text-neutral-500 mt-3">
              Refresh this page in a moment, or head back to your order.
            </p>
            {tx?.order_id && (
              <Link to={`/orders/${tx.order_id}`} className="btn-outline inline-flex mt-6">Back to order</Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
